import { FC } from "react";
import { IonCard, IonItem, useIonRouter } from "@ionic/react";
import { useTranslation } from "react-i18next";
import { IntegrationPartner } from "refsix-js-models";
import { assignmentPlatformRoute } from "../../../services/integrations/assignmentPlatformRoute";
import { visibleAssignmentPlatforms } from "../../../services/integrations/visibleAssignmentPlatforms";

/**
 * One scraped platform's tile on the Integrations page (REFSIX-568).
 *
 * A component rather than a loop so each platform sits in its own alphabetical
 * slot among the partner integrations, which are hand-listed.
 *
 * Renders nothing when the platform is withdrawn (`launched` false — the kill
 * switch) or has no page of its own, so the kill switch still works from here.
 *
 * It carries no platform mark and says "(Unofficial)": neither platform has
 * agreed to anything, and showing their own logo is the part that reads as
 * endorsement. A country flag is not a platform mark — the partner tiles use
 * them the same way (DBU 🇩🇰, KNVB 🇳🇱).
 */
export const AssignmentPlatformTile: FC<{
    platformId: IntegrationPartner;
    flag?: string;
}> = ({ platformId, flag }) => {
    const { t } = useTranslation();
    const route = useIonRouter();

    const platform = visibleAssignmentPlatforms().find(
        (visible) => visible.id === platformId
    );
    const path = assignmentPlatformRoute(platformId);

    if (!platform || !path) {
        return null;
    }

    return (
        <IonCard>
            <IonItem
                button
                lines="none"
                data-testid={`button-integration-${platformId}`}
                onClick={() => route.push(path)}
            >
                {t("integrations.assignments.unofficialName", {
                    platform: platform.displayName,
                })}
                {flag && <>&nbsp;{flag}</>}
            </IonItem>
        </IonCard>
    );
};
